import React, { useState, useEffect } from 'react';
import axios from 'axios';

const SHIPMENT_OVERVIEW_COLUMNS = [
    { key: "courier", label: "Courier Name" },
    { key: "manifested", label: "Manifested" },
    { key: "inTransit", label: "In-Transit" },
    { key: "pending", label: "Pending" },
    { key: "ofd", label: "OFD" },
    { key: "delivered", label: "Delivered" },
    { key: "rto", label: "RTO" },
    { key: "lost", label: "Lost" },
    { key: "totalShipment", label: "Total Shipment" }
];

const OverviewCardSection = ({ selectedUserId }) => {
    const [dashdata, setDashData] = useState({
        ordersByZone: [],
        revenueStats: {},
        weightSplit: [],
        weightByCourier: [],
        shipmentByCourier: [], // <-- Add this for the table
    });

    useEffect(() => {
        const fetchData = async () => {
            try {
                const [session, token] = document.cookie.split("=");
                const res = await axios.get(
                    `${process.env.REACT_APP_BACKEND_URL}/dashboard/getOverviewCardData`,
                    {
                        params: selectedUserId ? { userId: selectedUserId } : {},
                        headers: {
                            Authorization: `Bearer ${token}`,
                        },
                    }
                );
                if (res.data.success) {
                    setDashData(res.data.data);
                }
            } catch (err) {
                console.error('Failed to fetch dashboard data:', err);
            }
        };
        fetchData();
    }, [selectedUserId]);

    const getZonePercentage = (label) => {
        const zone = dashdata?.ordersByZone?.find((z) => z.zone === label);
        return zone?.percentage || 0;
    };

    const shipmentsData = [
        { label: 'East', value: getZonePercentage('zoneA') },
        { label: 'West', value: getZonePercentage('zoneB') },
        { label: 'North', value: getZonePercentage('zoneC') },
        { label: 'South', value: getZonePercentage('zoneD') },
        { label: 'North East', value: getZonePercentage('zoneE') },
    ];

    const revenueData = [
        { label: 'Last 90 Days', value: `₹${dashdata.revenueStats?.last90Days || 0}` },
        { label: 'This Week', value: `₹${dashdata.revenueStats?.thisWeek || 0}` },
        { label: 'This Month', value: `₹${dashdata.revenueStats?.thisMonth || 0}` },
        { label: 'This Quarter', value: `₹${dashdata.revenueStats?.thisQuarter || 0}` },
    ];

    const weightByCourierData = dashdata.weightByCourier || [];

    // Shipment overview table data (limit 5)
    const shipmentData = Array.isArray(dashdata.shipmentByCourier) ? dashdata.shipmentByCourier.slice(0, 5) : [];

    return (
        <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4 my-4">
            {/* Shipments - Zone Distribution */}
            <div className="bg-white p-4 text-gray-700 shadow-sm border border-gray-200 rounded-lg">
                <p className="text-[12px] sm:text-[14px] font-[600] mb-4">Shipments - Zone Distribution</p>
                <ul>
                    {shipmentsData.map((zone, index) => (
                        <li
                            key={index}
                            className="flex justify-between p-2 text-gray-500 border-b border-gray-200 text-[12px] sm:text-[14px]"
                        >
                            <span>{zone.label}</span>
                            <span className="font-[600] text-gray-700 text-[12px] sm:text-[14px]">
                                {zone.value}%
                            </span>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Revenue */}
            <div className="bg-white p-4 text-gray-700 shadow-sm border border-gray-200 rounded-lg">
                <p className="text-[12px] sm:text-[14px] font-[600] mb-4">Revenue</p>
                <ul>
                    {revenueData.map((rev, index) => (
                        <li
                            key={index}
                            className="flex justify-between p-2 text-gray-500 border-b border-gray-200 text-[12px] sm:text-[14px]"
                        >
                            <span>{rev.label}</span>
                            <span className="font-[600] text-gray-700 text-[12px] sm:text-[14px]">{rev.value}</span>
                        </li>
                    ))}
                </ul>
            </div>

            {/* Weight Against Courier Partners */}
            <div className="bg-white p-4 text-gray-700 shadow-sm border border-gray-200 rounded-lg">
                <div className="flex justify-between items-center mb-4">
                    <p className="text-[13px] sm:text-[15px] font-semibold">Weight Against Courier Partners</p>
                    <span className="text-[10px] sm:text-[12px] text-gray-500">Last 30 days</span>
                </div>
                <ul className="space-y-2">
                    {weightByCourierData.length === 0 ? (
                        <li className="text-gray-400 text-[13px]">No data available</li>
                    ) : (
                        weightByCourierData.map((item, idx) => {
                            const colors = ["bg-[#0CBB7D]", "bg-blue-500", "bg-yellow-500", "bg-red-400", "bg-indigo-500", "bg-pink-500"];
                            const color = colors[idx % colors.length];
                            return (
                                <li
                                    key={idx}
                                    className="flex justify-between items-center text-[12px] sm:text-[14px] text-gray-700"
                                >
                                    <div className="flex items-center gap-2">
                                        <span className="w-4 h-4 border border-gray-400 rounded-sm flex items-center justify-center">
                                            <span className={`w-2.5 h-2.5 ${color} rounded-sm`} />
                                        </span>
                                        {item.courier}
                                    </div>
                                    <span className="px-2 py-0.5 text-[11px] rounded bg-gray-100 text-gray-600 font-medium">
                                        {item.weight} kg
                                    </span>
                                </li>
                            );
                        })
                    )}
                </ul>
            </div>

            {/* --- NEW SHIPMENT OVERVIEW BY COURIER TABLE (Full screen, responsive) --- */}
            <div className="col-span-full mt-2">
                <div className="rounded-xl px-3 py-4 w-full bg-white shadow-md">
                    <div className="mb-1">
                        <p className="font-semibold text-[16px] text-gray-700">Shipments by Courier</p>
                    </div>
                    {/* Desktop Table */}
                    <div className="hidden sm:block">
                        <table className="w-full text-[14px]">
                            <thead>
                                <tr className="bg-gray-50">
                                    {SHIPMENT_OVERVIEW_COLUMNS.map((col) => (
                                        <th
                                            key={col.key}
                                            className="py-3 px-2 text-center font-bold text-black"
                                        >
                                            {col.label}
                                        </th>
                                    ))}
                                </tr>
                            </thead>
                            <tbody>
                                {shipmentData.length === 0 ? (
                                    <tr>
                                        <td colSpan={SHIPMENT_OVERVIEW_COLUMNS.length} className="text-center py-7 text-gray-400 font-medium">
                                            No data available
                                        </td>
                                    </tr>
                                ) : (
                                    shipmentData.map((row, i) => (
                                        <tr key={i} className="border-b border-gray-100 last:border-0">
                                            {SHIPMENT_OVERVIEW_COLUMNS.map((col, idx) => (
                                                <td key={col.key} className="py-2 px-2">
                                                    {row[col.key] ?? <span className="text-gray-300">-</span>}
                                                </td>
                                            ))}
                                        </tr>
                                    ))
                                )}
                            </tbody>
                        </table>
                    </div>
                    {/* Mobile Responsive Table */}
                    <div className="block sm:hidden">
                        {shipmentData.length === 0 ? (
                            <div className="text-gray-400 font-medium py-7 text-center">
                                No data available
                            </div>
                        ) : (
                            shipmentData.map((row, i) => (
                                <div key={i} className="mb-3 border-b border-gray-100 pb-2 last:pb-0 last:border-0">
                                    {SHIPMENT_OVERVIEW_COLUMNS.map((col, idx) => (
                                        <div key={col.key} className="flex text-[13px] py-1">
                                            <div className="w-[55%] font-bold text-gray-800">{col.label}</div>
                                            <div className="w-[45%] text-gray-700 pl-2 break-all">{row[col.key] ?? <span className="text-gray-300">-</span>}</div>
                                        </div>
                                    ))}
                                </div>
                            ))
                        )}
                    </div>
                </div>
            </div>
            {/* --- END SHIPMENT OVERVIEW TABLE --- */}
        </div>
    );
};

export default OverviewCardSection;
